/**
 * spiritualMilestoneTracker.js
 *
 * Records and retrieves significant spiritual events in a person's journey.
 * Distinct from journey memory (process states) — milestones are ONE-TIME events.
 *
 * NOT gamification. Not a reward system. These are pastoral reference points
 * so Kevia remembers that this person went through a reconciliation, or made
 * a spiritual commitment, or returned after a long absence.
 *
 * Storage: localStorage — key per entity, 180-day TTL (milestones last longer).
 * Max 15 milestones per entity.
 *
 * Milestone types:
 *   reconciliacion       — relational reconciliation
 *   decision_espiritual  — spiritual commitment or re-commitment
 *   restauracion         — return after distance or spiritual drift
 *   regreso_iglesia      — physical return to church community
 *   apertura_emocional   — emotional opening after closure
 *   sanidad_relacional   — relational healing
 *   perseverancia        — sustained engagement through difficulty
 *   servicio_activo      — entering active ministry service
 *   inicio_estudios      — began catechism or spiritual studies
 *   bautismo             — baptism or baptismal preparation decision
 */

const KEY_PREFIX   = "kevia_milestones_v1_";
const MILESTONE_TTL = 180 * 24 * 60 * 60 * 1000;  // 180 days
const MAX_PER_ENTITY = 15;
const MAX_ENTITIES   = 60;

const MILESTONE_LABELS = {
  reconciliacion:      "reconciliación",
  decision_espiritual: "decisión espiritual",
  restauracion:        "restauración",
  regreso_iglesia:     "regreso a la iglesia",
  apertura_emocional:  "apertura emocional",
  sanidad_relacional:  "sanidad relacional",
  perseverancia:       "perseverancia",
  servicio_activo:     "servicio activo",
  inicio_estudios:     "inicio de estudios espirituales",
  bautismo:            "bautismo",
};

/**
 * @typedef {{
 *   ts:   number,
 *   type: string,
 *   note: string|null,
 * }} Milestone
 */

function _key(entityId) { return KEY_PREFIX + entityId; }

function _allKeys() {
  const keys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k?.startsWith(KEY_PREFIX)) keys.push(k);
  }
  return keys;
}

/**
 * Get all milestones for an entity, pruned by TTL.
 * @param {string} entityId
 * @returns {Milestone[]}
 */
export function getMilestones(entityId) {
  if (!entityId) return [];
  try {
    const raw = localStorage.getItem(_key(entityId));
    if (!raw) return [];
    const data = JSON.parse(raw);
    const now  = Date.now();
    return (data.milestones ?? []).filter((m) => now - m.ts < MILESTONE_TTL);
  } catch {
    return [];
  }
}

/**
 * Record a spiritual milestone for an entity.
 * Deduplicates the same type within 7 days.
 *
 * @param {string} entityId
 * @param {string} entityType
 * @param {string} name
 * @param {{ type: string, note?: string }} milestone
 */
export function recordMilestone(entityId, entityType, name, milestone) {
  if (!entityId || !milestone?.type) return;
  if (!MILESTONE_LABELS[milestone.type]) return;  // only known types

  try {
    _pruneOverflow();
    const existing = getMilestones(entityId);

    // Deduplicate same type within 7 days
    const recent = existing.find((m) => {
      const daysAgo = (Date.now() - m.ts) / 86_400_000;
      return m.type === milestone.type && daysAgo < 7;
    });
    if (recent) return;

    const updated = [
      { ts: Date.now(), type: milestone.type, note: milestone.note ?? null },
      ...existing,
    ].slice(0, MAX_PER_ENTITY);

    localStorage.setItem(_key(entityId), JSON.stringify({ entityId, entityType, name, milestones: updated }));
  } catch {
    // localStorage quota — silent
  }
}

/**
 * Detect milestones implied by a spiritual process transition.
 * Called when process state changes significantly.
 *
 * @param {string} prevState
 * @param {string} currentState
 * @returns {string|null} milestone type or null
 */
export function detectImpliedMilestone(prevState, currentState) {
  if (!prevState || prevState === currentState) return null;

  // quebranto → restauración or apertura → reconciliation/restoration milestone
  if (
    (prevState === "quebranto" || prevState === "resistencia") &&
    (currentState === "restauración" || currentState === "apertura" || currentState === "crecimiento")
  ) {
    return "restauracion";
  }

  // búsqueda → apertura or crecimiento → spiritual decision
  if (prevState === "búsqueda" && (currentState === "apertura" || currentState === "crecimiento")) {
    return "decision_espiritual";
  }

  // resistencia → búsqueda → emotional opening
  if (prevState === "resistencia" && currentState === "búsqueda") {
    return "apertura_emocional";
  }

  return null;
}

/**
 * Build a compact instruction line from recent milestones.
 * Returns null if no milestones or all are too old to be relevant.
 *
 * @param {Milestone[]} milestones
 * @param {{ maxDays?: number, maxItems?: number }} opts
 * @returns {string|null}
 */
export function buildMilestonesInstruction(milestones, { maxDays = 60, maxItems = 3 } = {}) {
  if (!milestones?.length) return null;

  const now      = Date.now();
  const relevant = milestones
    .filter((m) => (now - m.ts) / 86_400_000 <= maxDays)
    .slice(0, maxItems);

  if (relevant.length === 0) return null;

  const labels = relevant.map((m) => MILESTONE_LABELS[m.type] ?? m.type);
  return `Hitos recientes en su proceso: ${labels.join(", ")}. Reconócelos con discreción.`;
}
